import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { useErLeder, BUTIKK_VALG } from './employeeAccess';
import BareLeder from './components/BareLeder';
import RolleValg from './components/RolleValg';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;


function EmployeeChange() {
    const { id } = useParams();
    const [formData, setFormData] = useState({
        navn: '',
        telefon: '',
        epost: '',
        tilgang: '',
        butikk: '', // Nytt felt for butikkvalg
        roller: [],
    });

    const navigate = useNavigate();
    const leder = useErLeder();
    const [sletter, setSletter] = useState(false);
    const [slettFeil, setSlettFeil] = useState('');

    useEffect(() => {
        const fetchEmployee = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/employees/${id}`);
                if (response.ok) {
                    const data = await response.json();
                    setFormData({ ...data, paPause: !!data.pauseFra });
                } else {
                    console.error('Feil ved henting av ansatt');
                }
            } catch (error) {
                console.error('Feil ved kommunikasjon med serveren:', error);
            }
        };

        fetchEmployee();
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const response = await fetch(`${API_BASE_URL}/employees/${id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                navigate('/employee-list');
            } else {
                console.error('Feil ved oppdatering av ansatt');
            }
        } catch (error) {
            console.error('Feil ved kommunikasjon med serveren:', error);
        }
    };

    // Sletter den ansatte for godt (backend: bareLeder, ikke seg selv). Sakene beholder navnet.
    const handleDelete = async () => {
        const navn = formData.navn || 'den ansatte';
        const ok = window.confirm(
            `Slette ${navn}?\n\nDen ansatte blir slettet helt fra systemet og kan ikke gjenopprettes. ` +
            'Saker og logger beholder navnet som tekst.\n\n' +
            'Vil du bare ta den ansatte ut av listene, velg «Ikke aktiv» under Butikk i stedet.'
        );
        if (!ok) return;
        setSletter(true);
        setSlettFeil('');
        try {
            const response = await fetch(`${API_BASE_URL}/employees/${id}`, {
                method: 'DELETE',
                headers: { 'X-Ansatt': encodeURIComponent(Cookies.get('selectedEmployee') || '') },
            });
            const svar = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(svar.message || 'Kunne ikke slette den ansatte');
            navigate('/employee-list');
        } catch (error) {
            setSlettFeil(error.message);
            setSletter(false);
        }
    };

    if (!leder) return <BareLeder tekst="Bare en leder kan endre ansatte." />;

    return (
        <div className="max-w-5xl mx-auto py-8 bg-white shadow-lg rounded-lg p-6 mb-4">
            <h2 className="text-3xl font-bold mb-6 text-center">Endre Ansatt</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label htmlFor="ansatt-navn" className="block text-sm font-medium text-gray-700">Navn</label>
                    <input
                        type="text"
                        id="ansatt-navn"
                        name="navn"
                        value={formData.navn}
                        onChange={handleChange}
                        required
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"
                    />
                </div>
                <div>
                    <label htmlFor="ansatt-telefon" className="block text-sm font-medium text-gray-700">Telefon</label>
                    <input
                        type="tel"
                        id="ansatt-telefon"
                        name="telefon"
                        value={formData.telefon}
                        onChange={handleChange}
                        required
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"
                    />
                </div>
                <div>
                    <label htmlFor="ansatt-epost" className="block text-sm font-medium text-gray-700">E-post</label>
                    <input
                        type="email"
                        id="ansatt-epost"
                        name="epost"
                        value={formData.epost}
                        onChange={handleChange}
                        required
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"
                    />
                </div>
                <div>
                    <label htmlFor="ansatt-tilgang" className="block text-sm font-medium text-gray-700">Tilgang</label>
                    <select
                        id="ansatt-tilgang"
                        name="tilgang"
                        value={formData.tilgang || 'ansatt'} // ikke satt = ansatt (employeeAccess.js)
                        onChange={handleChange}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"
                    >
                        <option value="ansatt">Ansatt</option>
                        <option value="leder">Leder</option>
                    </select>
                </div>
                <RolleValg
                    roller={formData.roller || []}
                    onChange={(roller) => setFormData((prevData) => ({ ...prevData, roller }))}
                />
                <div>
                    <label htmlFor="ansatt-butikk" className="block text-sm font-medium text-gray-700">Butikk</label>
                    <select
                        id="ansatt-butikk"
                        name="butikk"
                        value={formData.butikk}
                        onChange={handleChange}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"
                    >
                        <option value="">Velg butikk</option>
                        {BUTIKK_VALG.map((b) => <option key={b} value={b}>{b}</option>)}
                        <option value="Ingen">Ikke aktiv</option>

                    </select>
                </div>
                <div className="rounded-md border border-slate-300 bg-slate-50 p-3">
                    <label className="flex items-start gap-2 text-sm text-gray-800">
                        <input
                            type="checkbox"
                            checked={!!formData.paPause}
                            onChange={(e) => setFormData((prevData) => ({ ...prevData, paPause: e.target.checked }))}
                            aria-describedby="ansatt-pause-hjelp"
                            className="mt-0.5 h-4 w-4"
                        />
                        <span className="font-medium">
                            På pause
                            {formData.pauseFra && formData.paPause && (
                                <span className="font-normal text-gray-600"> (siden {new Date(formData.pauseFra).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short', year: 'numeric' })})</span>
                            )}
                        </span>
                    </label>
                    <p id="ansatt-pause-hjelp" className="mt-1 text-sm text-gray-600">
                        For den som ikke har sluttet, men ikke skal ha nyheter, klubbinfo eller SMS nå, f.eks. permisjon
                        eller utenom sesong. Hen kan fortsatt velge seg selv i menyen. En leder kan likevel krysse hen av
                        for én nyhet. «Ikke aktiv» under Butikk tas ut av menyen også.
                    </p>
                </div>
                <div className="text-center">
                    <button type="submit" className="bg-green-700 text-white px-6 py-2 rounded hover:bg-green-800">
                        Oppdater Ansatt
                    </button>
                </div>
            </form>
            <section aria-labelledby="slett-ansatt-tittel" className="mt-10 rounded-lg border border-red-200 bg-red-50 p-4">
                <h3 id="slett-ansatt-tittel" className="text-lg font-semibold text-red-900">Slett ansatt</h3>
                <p className="mt-1 text-sm text-red-900">
                    For ansatte som har sluttet. Den ansatte blir slettet helt fra systemet og kan ikke gjenopprettes.
                </p>
                {slettFeil && (
                    <p role="alert" className="mt-2 text-sm font-semibold text-red-800">{slettFeil}</p>
                )}
                <button
                    type="button"
                    onClick={handleDelete}
                    disabled={sletter}
                    className="mt-3 rounded bg-red-700 px-4 py-2 font-semibold text-white hover:bg-red-800 disabled:opacity-60"
                >
                    {sletter ? 'Sletter …' : 'Slett ansatt'}
                </button>
            </section>
        </div>
    );
}

export default EmployeeChange;
